import { useAutoApplyRuns, useSafetyStatus, useTriggerAutoApply } from '@/api/automation'
import { Play, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function AutoApplyTab() {
  const { data: runsData, isLoading } = useAutoApplyRuns({ limit: 20 })
  const { data: safetyData } = useSafetyStatus()
  const trigger = useTriggerAutoApply()

  const runs = runsData?.runs ?? []
  const enabled = safetyData?.autoApplyEnabled ?? false

  return (
    <div className="space-y-4">
      {/* Status-Banner */}
      <div className={cn(
        'rounded-lg border p-4',
        enabled
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-amber-200 bg-amber-50',
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {enabled
              ? <CheckCircle className="h-5 w-5 text-emerald-600" />
              : <AlertTriangle className="h-5 w-5 text-amber-600" />
            }
            <div>
              <p className="text-sm font-semibold">
                Auto-Apply: {enabled ? 'Aktiv' : 'Deaktiviert'}
              </p>
              <p className="text-xs text-[var(--muted-foreground)]">
                {enabled
                  ? 'Regeln mit Auto-Apply werden taeglich um 09:00 ausgefuehrt.'
                  : 'GLOBAL_AUTO_APPLY_ENABLED ist nicht gesetzt. Setze die ENV-Variable auf "true" um Auto-Apply zu aktivieren.'
                }
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              if (!enabled) {
                alert('Auto-Apply ist global deaktiviert. Setze GLOBAL_AUTO_APPLY_ENABLED=true in der .env-Datei.')
                return
              }
              if (!confirm('Auto-Apply jetzt manuell ausfuehren?')) return
              trigger.mutate()
            }}
            disabled={trigger.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white',
              enabled
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-zinc-400 cursor-not-allowed',
              'disabled:opacity-50',
            )}
          >
            <Play className="h-3.5 w-3.5" />
            {trigger.isPending ? 'Laeuft...' : 'Manuell ausfuehren'}
          </button>
        </div>
      </div>

      {/* Safety-Caps Info */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Max Bid-Delta', value: '30%' },
          { label: 'Bid-Cooldown', value: '48h' },
          { label: 'Rollback-Window', value: '24h' },
          { label: 'Max/Run', value: '50 Events' },
        ].map(cap => (
          <div key={cap.label} className="rounded-md border border-[var(--border)] bg-[var(--card)] p-3 text-center">
            <p className="text-xs text-[var(--muted-foreground)]">{cap.label}</p>
            <p className="text-lg font-bold">{cap.value}</p>
          </div>
        ))}
      </div>

      {/* Run-History */}
      <div>
        <h2 className="mb-2 text-sm font-semibold">Auto-Apply Runs</h2>

        <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[var(--border)] bg-[var(--muted)]">
              <tr>
                <th className="px-3 py-2 text-left font-medium">ID</th>
                <th className="px-3 py-2 text-left font-medium">Gestartet</th>
                <th className="px-3 py-2 text-left font-medium">Abgeschlossen</th>
                <th className="px-3 py-2 text-right font-medium">Regeln</th>
                <th className="px-3 py-2 text-right font-medium">Applied</th>
                <th className="px-3 py-2 text-right font-medium">Failed</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[var(--muted-foreground)]">Lade...</td></tr>
              )}
              {!isLoading && runs.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[var(--muted-foreground)]">Noch keine Auto-Apply Runs</td></tr>
              )}
              {runs.map(run => {
                const hasErrors = (run.errors?.length ?? 0) > 0
                const isComplete = !!run.completedAt
                return (
                  <tr key={run.id} className="border-b border-[var(--border)] hover:bg-[var(--muted)]/50">
                    <td className="px-3 py-2 font-mono text-xs">#{run.id}</td>
                    <td className="px-3 py-2 text-xs">
                      {new Date(run.startedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {run.completedAt
                        ? new Date(run.completedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
                        : <span className="text-amber-600">Laeuft...</span>
                      }
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{run.rulesEvaluated}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className={run.eventsApplied > 0 ? 'text-emerald-600 font-medium' : ''}>
                        {run.eventsApplied}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className={run.eventsFailed > 0 ? 'text-red-600 font-medium' : ''}>
                        {run.eventsFailed}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {!isComplete && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Running</span>
                      )}
                      {isComplete && !hasErrors && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                          <CheckCircle className="h-3 w-3" />OK
                        </span>
                      )}
                      {isComplete && hasErrors && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-800" title={run.errors?.map(e => e.error).join('\n')}>
                          <XCircle className="h-3 w-3" />{run.eventsFailed} Fehler
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
